import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Typography,
    Box,
    Divider,
    IconButton,
    TextField,
    Stack,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";

const ProfileGameStats = () => {
    const [data, setData] = useState({
        xp: "0",
        kmWalked: "0",
        pokemonCaught: "0",
        pokestopsSpun: "0",
    });

    const [tempData, setTempData] = useState(data);
    const [editMode, setEditMode] = useState({
        xp: false,
        kmWalked: false,
        pokemonCaught: false,
        pokestopsSpun: false,
    });

    const fieldMap = {
        xp: "xp",
        kmWalked: "km_walked",
        pokemonCaught: "pokemon_caught",
        pokestopsSpun: "pokestops_spun",
    };

    useEffect(() => {
        const fetchGameStats = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await axios.get("/api/users/game-stats", {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const newData = {
                    xp: String(response.data.xp ?? 0),
                    kmWalked: String(response.data.km_walked ?? 0),
                    pokemonCaught: String(response.data.pokemon_caught ?? 0),
                    pokestopsSpun: String(response.data.pokestops_spun ?? 0),
                };

                setData(newData);
                setTempData(newData);
            } catch (err) {
                console.error("Fehler beim Laden der Spieldaten:", err);
            }
        };

        fetchGameStats();
    }, []);

    const handleEdit = (key) => {
        setEditMode({ ...editMode, [key]: true });
    };

    const handleCancel = (key) => {
        setTempData({ ...tempData, [key]: data[key] });
        setEditMode({ ...editMode, [key]: false });
    };

    const handleChange = (key, value) => {
        if (key === "kmWalked") {
            // Nur Zahlen und maximal ein Komma oder Punkt erlauben
            value = value.replace(",", ".").replace(/[^0-9.]/g, "");
            const parts = value.split(".");
            if (parts.length > 2) return; // mehrere Punkte → ungültig
        } else {
            // Nur ganze Zahlen
            value = value.replace(/[^0-9]/g, "");
        }
        setTempData({ ...tempData, [key]: value });
    };

    const handleSave = async (key) => {
        try {
            const token = localStorage.getItem("token");
            const payloadKey = fieldMap[key];
            const payload = { [payloadKey]: tempData[key] };

            await axios.patch("/api/users/game-stats", payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setData({ ...data, [key]: tempData[key] });
            setEditMode({ ...editMode, [key]: false });
        } catch (err) {
            console.error("Fehler beim Speichern der Spieldaten:", err);
        }
    };

    const renderRow = (label, key) => (
        <Stack direction="row" alignItems="center" spacing={1} mb={2} key={key}>
            <TextField
                label={label}
                variant="outlined"
                size="small"
                value={tempData[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                InputProps={{
                    readOnly: !editMode[key],
                }}
                sx={{
                    flex: 1,
                    backgroundColor: (theme) =>
                        editMode[key]
                            ? theme.palette.background.paper
                            : theme.palette.action.disabledBackground,
                    transition: "background-color 0.3s",
                }}
            />
            {editMode[key] ? (
                <>
                    <IconButton onClick={() => handleSave(key)} aria-label="Speichern">
                        <SaveIcon />
                    </IconButton>
                    <IconButton onClick={() => handleCancel(key)} aria-label="Abbrechen">
                        <UndoIcon />
                    </IconButton>
                </>
            ) : (
                <IconButton onClick={() => handleEdit(key)} aria-label="Bearbeiten">
                    <EditIcon />
                </IconButton>
            )}
        </Stack>
    );

    return (
        <Box mb={4}>
            <Typography variant="h5" gutterBottom align="center">
                Spieldaten
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {renderRow("Erfahrungspunkte (XP)", "xp")}
            {renderRow("Gelaufene Kilometer", "kmWalked")}
            {renderRow("Gefangene Pokémon", "pokemonCaught")}
            {renderRow("Gedrehte Pokéstops", "pokestopsSpun")}
        </Box>
    );
};

export default ProfileGameStats;
